// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import { DetachTagCategoryRequest, DetachTagCategoryResponse, } from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DetachTagCategoryRpc = (request: DetachTagCategoryRequest) => Observable<DetachTagCategoryResponse>

export const detachTagCategory = (detachTagCategoryRpc: DetachTagCategoryRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.detachTagCategory.match),
      mergeMap(({ payload: detachTagCategoryRequest }) =>
        of(detachTagCategoryRequest)
          .pipe(
            mergeMap(detachTagCategoryRpc),
            mergeMap(_ => of(TimelineSlice.detachTagCategory({
              timeRowIds: detachTagCategoryRequest.getTimeRowIds()?.getIdsList(),
              timeGroupId: detachTagCategoryRequest.getTimeGroupId(),
              tagCategoryId: detachTagCategoryRequest.getTagCategoryId(),
            }))),
            catchError(handleError),
          )
      ),
    )

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_detach_tag_category_failed_precondition",
            message: "Failed to detach tag category. Time row may be out of sync."
          }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_detach_tag_category_failed_permission_denied",
          message: "Failed to detach tag category. You do not have permission to detach a tag category from the time row or time group." }),
        messageType: "ERROR",
      }))
    }
  }

  return of(showSnackBar({
    message: t({
      id: "msg_detach_tag_category_failed",
      message: "Failed to detach tag category. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const detachTagCategoryRpc = (request: DetachTagCategoryRequest): Observable<DetachTagCategoryResponse> =>
  grpc(() => timelineClient.detachTagCategory(request, null))

export default detachTagCategory(detachTagCategoryRpc)
